import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RenderedOutputSourceArtifactSlim } from 'situview-webapi-client';

@Component({
  selector: 'source-artifacts-menu',
  templateUrl: './source-artifacts-menu.component.html',
  styleUrls: ['./source-artifacts-menu.component.less']
})
export class SourceArtifactsMenuComponent implements OnChanges {
  /* standard */
  @Input() isAdmin: boolean = false;
  @Input() isSituUser: boolean = false;
  @Input() isDarkMode: boolean = false;
  
  @Input() sourceArtifacts: RenderedOutputSourceArtifactSlim[] | undefined;
  @Input() secondSourceArtifacts: RenderedOutputSourceArtifactSlim[] | undefined;
  @Input() colourOverride: string | undefined;
  filteredArtifacts: GroupedArtifacts[] = [];

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.sourceArtifacts?.currentValue) {
      this.filterAndGroupArtifacts();
    }
  } 

  filterAndGroupArtifacts(): void {
    //console.log(this.sourceArtifacts);
    if (!this.sourceArtifacts) return;
  
    const filteredArtifacts: RenderedOutputSourceArtifactSlim[] = this.sourceArtifacts.filter(
      (artifact: RenderedOutputSourceArtifactSlim) => {
        return (
          artifact.callerReferenceKey?.startsWith('software\\') ||
          artifact.callerReferenceKey?.startsWith('ifc\\') ||
          artifact.callerReferenceKey?.startsWith('assetmodule\\') ||
          artifact.callerReferenceKey?.startsWith('placement\\') ||
          artifact.callerReferenceKey?.startsWith('builderifcsettings\\')
        );
      }
    );
  
    const groupedArtifacts: { [key: string]: RenderedOutputSourceArtifactSlimExtended[] } = {};
  
    filteredArtifacts.forEach((artifact: RenderedOutputSourceArtifactSlim) => {
      let groupKey = '';
      let modifiedCallerReferenceKey = '';
  
      if (artifact.callerReferenceKey?.startsWith('software\\')) {
        groupKey = 'software';
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr('software\\'.length);
      } else if (artifact.callerReferenceKey?.startsWith('ifc\\')) {
        groupKey = 'ifc';
        const secondBackslashIndex = artifact.callerReferenceKey.indexOf('\\', artifact.callerReferenceKey.indexOf('\\') + 1);
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr(secondBackslashIndex + 1);
      } else if (artifact.callerReferenceKey?.startsWith('assetmodule\\')) {
        groupKey = 'themes';
        const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
        modifiedCallerReferenceKey = artifact.callerReferenceKey.substr(lastBackslashIndex + 1);
      } else {
        groupKey = 'other';
        if (artifact.callerReferenceKey) {
          if (artifact.callerReferenceKey?.startsWith('placement\\')) {
            const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
            modifiedCallerReferenceKey = `placement - ${artifact.callerReferenceKey.substr(lastBackslashIndex + 1)}`;
          }
          if (artifact.callerReferenceKey?.startsWith('builderifcsettings\\')) {
            const lastBackslashIndex = artifact.callerReferenceKey.lastIndexOf('\\');
            modifiedCallerReferenceKey = "ifcsettings";
          }
        }
      }
  
      let extendedArtifact = artifact as RenderedOutputSourceArtifactSlimExtended;
      extendedArtifact.callerReferenceKey = modifiedCallerReferenceKey;
      extendedArtifact.displayText = this.getDisplayedText(groupKey, artifact);
      extendedArtifact.isClickable = this.isClickable(groupKey);
      extendedArtifact.routes = this.getRouterLink(groupKey, artifact);
      extendedArtifact.queryParams = this.getQueryParams(groupKey, artifact);
  
      if (!groupedArtifacts[groupKey]) {
        groupedArtifacts[groupKey] = [];
      }
  
      if (groupKey === 'software' && this.secondSourceArtifacts) {
        const secondArtifact = this.secondSourceArtifacts.find(
          (secondArtifact: RenderedOutputSourceArtifactSlim) =>
            secondArtifact.callerReferenceKey === artifact.callerReferenceKey
        );
  
        if (secondArtifact && secondArtifact.version !== artifact.version) {
          extendedArtifact.isDifferentVersion = true;
        }
      }
  
      groupedArtifacts[groupKey].push(extendedArtifact);
    });
  
    this.filteredArtifacts = [
      { groupKey: 'ifc', artifacts: groupedArtifacts['ifc'] || [] },
      { groupKey: 'software', artifacts: groupedArtifacts['software'] || [] },
      { groupKey: 'themes', artifacts: groupedArtifacts['themes'] || [] },
      { groupKey: 'other', artifacts: groupedArtifacts['other'] || [] },
    ];
  }

  isClickable(groupKey: string): boolean {
    return groupKey === 'ifc';
  }
  
  getRouterLink(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): any[] {
    if (!artifact?.callerReferenceKey) return [];
    
    if (groupKey === 'ifc') {
      const home = artifact.callerReferenceKey.split('\\')[0];
      return ['/homes/', home];
    }
    return [];
  }
  
  getQueryParams(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): any {
    if (!artifact?.callerReferenceKey) return {};
    
    if (groupKey === 'ifc') {
      const ifcFile = artifact.callerReferenceKey.split('\\')[2];
      return { ifcFile: ifcFile, v: artifact.version };
    }
    return {};
  }
  
  getDisplayedText(groupKey: string, artifact: RenderedOutputSourceArtifactSlim): string {
    if (!artifact?.callerReferenceKey) return "";
    
    if (groupKey === 'ifc') {
      return artifact.callerReferenceKey.split('\\')[2];
    }
    return artifact.callerReferenceKey;
  }
}

interface GroupedArtifacts {
  groupKey: string;
  artifacts: RenderedOutputSourceArtifactSlimExtended[];
}

class RenderedOutputSourceArtifactSlimExtended extends RenderedOutputSourceArtifactSlim {
  queryParams: any = {};
  routes: any[] = [];
  isClickable: boolean = false;
  displayText: string = "";
  isDifferentVersion?: boolean = false;
}